/**
 * Copyright 2023-2026 Nordcloud Oy or its affiliates. All Rights Reserved.
 */

import { useMemo } from "react";
import { When } from "react-if";
import {
  Box,
  ExtendedTooltip,
  FlexContainer,
  Text,
  theme,
} from "@nordcloud/gnui";
import { EventActionResourceReport } from "~/generated/graphql";
import { getResourceStatusConfig } from "~/components/resourceGroup/utils";
import { StyledSVGIcon } from "~/components/Styled/StyledSVGIcon";
import { NameWithLink } from "~/components/Tables/cells/NameWithLink";
import { ROUTES } from "~/routing/routes";
import { EventResourceExecutionErrorMessage } from "~/views/events/EventsDetails/Tabs/Details/components/ActionBox/components/EventResourceExecutionErrorMessage";
import { LogsOutputButton } from "./LogOutputButton";
import { ResourceDetails } from "./ResourceDetails";

type Props = {
  eventActionResourceReport: EventActionResourceReport;
};

export function ResourceStatus({ eventActionResourceReport }: Props) {
  const result = eventActionResourceReport.result;
  const logOutputLabel = `${eventActionResourceReport.resourceId} - ${eventActionResourceReport.name}`;
  const statusConfig = useMemo(
    () => getResourceStatusConfig(eventActionResourceReport.status),
    [eventActionResourceReport.status]
  );

  return (
    <FlexContainer direction="column" css={{ width: "100%" }}>
      <Box css={{ borderRadius: 0 }}>
        <FlexContainer justifyContent="space-between">
          <div css={{ marginRight: theme.spacing.spacing03 }}>
            <ExtendedTooltip
              caption={statusConfig.description}
              status={statusConfig.tooltipColor}
              position="start"
            >
              <StyledSVGIcon
                name={statusConfig.icon}
                color={statusConfig.color()}
                pathColors={statusConfig.pathColors}
              />
            </ExtendedTooltip>
          </div>
          <div css={{ flex: 1 }}>
            <NameWithLink
              link={ROUTES.resources.generateDetails(
                eventActionResourceReport.resourceId ?? ""
              )}
              name={
                eventActionResourceReport.name ??
                eventActionResourceReport.resourceId
              }
            />
            <Text
              css={{ wordBreak: "break-word" }}
              size="sm"
              color={theme.color.text.text02}
            >
              {eventActionResourceReport.resourceId}
            </Text>
          </div>
          <LogsOutputButton
            outputUri={result.outputUri}
            actionLogLabel={logOutputLabel}
            asIcon
          />
        </FlexContainer>
      </Box>
      <When condition={result !== null}>
        <Box
          boxStyle="lightGrey"
          css={{ borderRadius: 0 }}
          mb={theme.spacing.spacing04}
        >
          <EventResourceExecutionErrorMessage
            errorCode={eventActionResourceReport.errorCode}
          />
          <ResourceDetails {...{ result }} />
        </Box>
      </When>
    </FlexContainer>
  );
}
